import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, Mail, Phone } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { MaskLines } from "../lib/motion";
import { ArcButton } from "../components/site/Brand";
import { SITE, SERVICE_OPTIONS, PROPERTY_TYPES, BUDGET_RANGES, TIMELINES, REFERRALS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children, required, error, htmlFor }) => (
  <div className="flex flex-col">
    <label htmlFor={htmlFor} className="overline text-olive mb-3 underline underline-offset-4 decoration-1">
      {label}{required && <span className="text-bronze"> *</span>}
    </label>
    {children}
    {error && <span className="mt-2 text-xs text-destructive" role="alert" data-testid={`error-${htmlFor}`}>{error}</span>}
  </div>
);

const inputCls =
  "w-full bg-transparent border-b border-charcoal/25 py-3 font-light text-charcoal placeholder:text-olive/40 focus:outline-none focus:border-bronze transition-colors duration-300";
const selectCls = `${inputCls} appearance-none cursor-pointer`;

const initial = {
  name: "", email: "", phone: "", location: "", property_type: "",
  service: "", budget: "", timeline: "", referral: "", message: "",
  consent: false, website: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please tell us your name.";
    if (!form.email.trim()) e.email = "An email address is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.message.trim()) e.message = "A short description helps us prepare.";
    if (!form.consent) e.consent = "Please confirm you are happy for us to be in touch.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      const { website, ...rest } = form;
      // drop empty optional fields so we don't store blank strings
      const payload = Object.fromEntries(
        Object.entries(rest).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      await axios.post(`${API}/enquiries`, { ...payload, website });
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <>
      <Seo
        title="Start a conversation | Arc & Hollow — London"
        description="Share a little about your property and ambitions, and Arc & Hollow will be in touch to arrange an initial conversation."
        path="/contact"
      />

      <section className="container-x pt-36 md:pt-52 pb-16 md:pb-20">
        <p className="overline text-bronze mb-8 underline underline-offset-[6px] decoration-1">Contact</p>
        <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
          <MaskLines lines={["Start a conversation."]} />
        </h1>
        <p className="mt-10 max-w-2xl text-lg font-light leading-relaxed text-olive">
          Tell us a little about your property, the scope of work you are considering and your
          intended timeline. We will review your enquiry and contact you to arrange an initial
          conversation.
        </p>
      </section>

      <section className="container-x pb-28">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-8 order-2 lg:order-1">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-charcoal text-chalk p-10 md:p-14"
                  data-testid="enquiry-success"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center border border-bronze text-bronze mb-8">
                    <Check size={22} strokeWidth={1.5} />
                  </span>
                  <h2 className="font-serif text-3xl md:text-4xl font-light">Thank you for your enquiry.</h2>
                  <p className="mt-6 max-w-lg text-chalk/70 font-light leading-relaxed">
                    We will review the information provided and be in touch shortly.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <ArcButton to="/projects" variant="primary" onDark>Explore our work</ArcButton>
                    <button
                      onClick={() => { setForm(initial); setStatus("idle"); }}
                      className="overline text-chalk/80 hover:text-chalk link-underline"
                      data-testid="enquiry-reset"
                    >
                      Send another enquiry
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={submit}
                  noValidate
                  className="grid gap-8 sm:grid-cols-2"
                  data-testid="enquiry-form"
                >
                  {/* Honeypot — hidden from users */}
                  <input
                    type="text" tabIndex={-1} autoComplete="off"
                    value={form.website} onChange={set("website")}
                    className="hidden" aria-hidden="true"
                  />

                  <Field label="Name" htmlFor="name" required error={errors.name}>
                    <input id="name" data-testid="input-name" className={inputCls} value={form.name} onChange={set("name")} />
                  </Field>
                  <Field label="Email" htmlFor="email" required error={errors.email}>
                    <input id="email" type="email" data-testid="input-email" className={inputCls} value={form.email} onChange={set("email")} />
                  </Field>
                  <Field label="Telephone (optional)" htmlFor="phone">
                    <input id="phone" data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} />
                  </Field>
                  <Field label="Project location" htmlFor="location">
                    <input id="location" data-testid="input-location" placeholder="e.g. Chelsea, London" className={inputCls} value={form.location} onChange={set("location")} />
                  </Field>
                  <Field label="Property type" htmlFor="property_type">
                    <select id="property_type" data-testid="input-property" className={selectCls} value={form.property_type} onChange={set("property_type")}>
                      <option value="">Select…</option>
                      {PROPERTY_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Type of service" htmlFor="service">
                    <select id="service" data-testid="input-service" className={selectCls} value={form.service} onChange={set("service")}>
                      <option value="">Select…</option>
                      {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Approximate budget" htmlFor="budget">
                    <select id="budget" data-testid="input-budget" className={selectCls} value={form.budget} onChange={set("budget")}>
                      <option value="">Select…</option>
                      {BUDGET_RANGES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Desired timeline" htmlFor="timeline">
                    <select id="timeline" data-testid="input-timeline" className={selectCls} value={form.timeline} onChange={set("timeline")}>
                      <option value="">Select…</option>
                      {TIMELINES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="How did you hear about us?" htmlFor="referral">
                    <select id="referral" data-testid="input-referral" className={selectCls} value={form.referral} onChange={set("referral")}>
                      <option value="">Select…</option>
                      {REFERRALS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Project description" htmlFor="message" required error={errors.message}>
                      <textarea id="message" data-testid="input-message" rows={5} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about the property, your ambitions and anything that matters to you." />
                    </Field>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="flex items-start gap-3 cursor-pointer" htmlFor="consent">
                      <input id="consent" type="checkbox" data-testid="input-consent" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-charcoal shrink-0" />
                      <span className="text-sm font-light text-olive leading-relaxed">
                        I am happy for Arc &amp; Hollow to contact me about my enquiry and have read the{" "}
                        <Link to="/privacy-policy" className="link-underline text-charcoal">Privacy Policy</Link>.
                      </span>
                    </label>
                    {errors.consent && <span className="mt-2 text-xs text-destructive block" role="alert" data-testid="error-consent">{errors.consent}</span>}
                  </div>

                  {status === "error" && (
                    <p className="sm:col-span-2 text-sm text-destructive" role="alert" data-testid="enquiry-error">
                      We were unable to send your enquiry. Please try again or email {SITE.email} directly.
                    </p>
                  )}

                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={status === "sending"}
                      data-testid="enquiry-submit"
                      className="inline-flex items-center justify-center gap-3 bg-charcoal text-chalk px-10 py-4 overline underline underline-offset-4 decoration-1 hover:bg-bronze transition-colors duration-500 disabled:opacity-60 active:scale-[0.98]"
                    >
                      {status === "sending" ? (<><Loader2 size={15} className="animate-spin" /> Sending…</>) : "Send enquiry"}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          <aside className="lg:col-span-4 order-1 lg:order-2">
            <div className="mb-10">
              <p className="overline text-bronze underline underline-offset-[6px] decoration-1 mb-4">Studio</p>
              <p className="font-light">{SITE.location}</p>
            </div>
            <div className="mb-10">
              <p className="overline text-bronze underline underline-offset-[6px] decoration-1 mb-4">Direct contact</p>
              <a href={`mailto:${SITE.email}`} aria-label={`Email Arc & Hollow at ${SITE.email}`} className="flex items-center gap-3 font-light py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal rounded-sm" data-testid="contact-email">
                <Mail size={16} strokeWidth={1.6} aria-hidden="true" className="text-bronze shrink-0" />
                <span className="link-underline whitespace-nowrap">{SITE.email}</span>
              </a>
              <a href={`tel:${SITE.phoneHref}`} aria-label={`Telephone Arc & Hollow on ${SITE.phone}`} className="flex items-center gap-3 font-light py-1.5 mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal rounded-sm" data-testid="contact-phone">
                <Phone size={16} strokeWidth={1.6} aria-hidden="true" className="text-bronze shrink-0" />
                <span className="link-underline whitespace-nowrap">{SITE.phone}</span>
              </a>
            </div>
            <div>
              <p className="overline text-bronze underline underline-offset-[6px] decoration-1 mb-4">Instagram</p>
              <a href={SITE.instagramUrl} target="_blank" rel="noreferrer noopener" className="font-light link-underline" data-testid="contact-instagram">{SITE.instagram}</a>
            </div>
            <p className="mt-12 text-sm font-light leading-relaxed text-olive/80">
              Arc &amp; Hollow works with a limited number of clients at any one time, allowing
              each commission the focus and attention its detail deserves.
            </p>
          </aside>
        </div>
      </section>
    </>
  );
}
